import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { CarHireFilterCategory } from 'constants/carHire'

function useCarHireLabels() {
  const intl = useIntl()

  return useMemo(() => {
    const pickupDropoffLocationSearchTitleLabel = intl.formatMessage({
      id: '7EUTA58gFU',
      defaultMessage: 'Pick-up & Drop-off location',
      description: 'Title label for combined pick-up and drop-off location search in car hire',
    })

    const dropoffLocationSearchTitleLabel = intl.formatMessage({
      id: 'n8MaItGMzL',
      defaultMessage: 'Drop-off location',
      description: 'Title label for drop-off location search in car hire',
    })

    const pickupLocationSearchTitleLabel = intl.formatMessage({
      id: 'RvoQxP13MC',
      defaultMessage: 'Pick-up location',
      description: 'Title label for pick-up location search in car hire',
    })

    const pickupLocationSearchPlaceholderLabel = intl.formatMessage({
      id: 'uf0B6LiPQ4',
      defaultMessage: 'Search for an airport or city',
      description: 'Placeholder text for car hire location search input',
    })

    const carHireLuggageCategories: Array<CarHireFilterCategory> = [{
      id: '1',
      label: intl.formatMessage({
        id: 'hnrOP0Kupj',
        defaultMessage: '0 - 2 bags',
        description: 'Car hire luggage filter category for 0 to 2 bags',
      }),
      min: 0,
      max: 2,
    }, {
      id: '2',
      label: intl.formatMessage({
        id: '4anbeqNVzL',
        defaultMessage: '3 - 4 bags',
        description: 'Car hire luggage filter category for 3 to 4 bags',
      }),
      min: 3,
      max: 4,
    }, {
      id: '3',
      label: intl.formatMessage({
        id: '7NzVcYiLAS',
        defaultMessage: '5 + bags',
        description: 'Car hire luggage filter category for 5 or more bags',
      }),
      min: 5,
      max: Number.MAX_SAFE_INTEGER,
    }]

    const carHireSeatCategories: Array<CarHireFilterCategory> = [{
      id: '1',
      label: intl.formatMessage({
        id: 'HDGFuABAIv',
        defaultMessage: '1 - 4',
        description: 'Car hire seat filter category for 1 to 4 seats',
      }),
      min: 1,
      max: 4,
    }, {
      id: '2',
      label: intl.formatMessage({
        id: 'p+zILBZUDW',
        defaultMessage: '5 - 6',
        description: 'Car hire seat filter category for 5 to 6 seats',
      }),
      min: 5,
      max: 6,
    }, {
      id: '3',
      label: intl.formatMessage({
        id: 'SzAY922Hgy',
        defaultMessage: '7 +',
        description: 'Car hire seat filter category for 7 or more seats',
      }),
      min: 7,
      max: Number.MAX_SAFE_INTEGER,
    }]

    const driverCategories: Array<{ id: App.CarHireDriverAgeCategory, label: string }> = [
      {
        id: 1,
        label: intl.formatMessage({
          id: 'q6GVzpeDlt',
          defaultMessage: '18 - 29',
          description: 'Car hire driver age category for ages 18 to 29',
        }),
      },
      {
        id: 2,
        label: intl.formatMessage({
          id: '0DiHCIq2/l',
          defaultMessage: '30 - 69',
          description: 'Car hire driver age category for ages 30 to 69',
        }),
      },
      {
        id: 3,
        label: intl.formatMessage({
          id: '4CNnP7sqlq',
          defaultMessage: '70+',
          description: 'Car hire driver age category for ages 70 and above',
        }),
      },
    ]

    const carHireFilterKeyToLabel = new Map<string, string>([
      ['luggage', intl.formatMessage({
        id: 'HUl4mOOrxl',
        defaultMessage: 'Luggage capacity',
        description: 'Car hire filter label for luggage capacity',
      })],
      ['supplier', intl.formatMessage({
        id: 'Ooe1thibmb',
        defaultMessage: 'Supplier',
        description: 'Car hire filter label for supplier',
      })],
      ['fuelType', intl.formatMessage({
        id: 'qYSW1s6Oqd',
        defaultMessage: 'Fuel type',
        description: 'Car hire filter label for fuel type',
      })],
      ['seats', intl.formatMessage({
        id: 't7V3FzJ74F',
        defaultMessage: 'Number of seats',
        description: 'Car hire filter label for number of seats',
      })],
      ['transmission', intl.formatMessage({
        id: 'y3z3ytqJNE',
        defaultMessage: 'Transmission',
        description: 'Car hire filter label for transmission type',
      })],
      ['pickUpType', intl.formatMessage({
        id: '6rMBIKOdrn',
        defaultMessage: 'Pick-up location',
        description: 'Car hire filter label for pick-up location type',
      })],
      ['mileage', intl.formatMessage({
        id: '+MkIE3YNXj',
        defaultMessage: 'Mileage',
        description: 'Car hire filter label for mileage option',
      })],
      ['carTypes', intl.formatMessage({
        id: 'eh3VfKa1if',
        defaultMessage: 'Car types',
        description: 'Car hire filter label for car types',
      })],
      ['payFullyNow', intl.formatMessage({
        id: 'cBGQGDFZ0+',
        defaultMessage: 'Pay in full',
        description: 'Car hire payment schedule label for paying in full now',
      })],
      ['payPartiallyNow', intl.formatMessage({
        id: 'gPG5O4mIXg',
        defaultMessage: 'Pay deposit',
        description: 'Car hire payment schedule label for paying a deposit',
      })],
      ['payFullyOnArrival', intl.formatMessage({
        id: 'j/fwBo9t8h',
        defaultMessage: 'Pay at pickup',
        description: 'Car hire payment schedule label for paying at pickup',
      })],
    ])

    const pickupTypeLabelMap = new Map<App.CarHirePickupType, string>([
      ['office', intl.formatMessage({
        id: 'NpQPKZIc1p',
        defaultMessage: 'Pick-up at rental office',
        description: 'Car hire pick-up type label for rental office location',
      })],
      ['shuttle', intl.formatMessage({
        id: 'Wl8+wb+JA0',
        defaultMessage: 'Pick-up by free shuttle',
        description: 'Car hire pick-up type label for free shuttle service',
      })],
      ['terminal', intl.formatMessage({
        id: 'dC1JBC72ap',
        defaultMessage: 'Pick-up in terminal',
        description: 'Car hire pick-up type label for in-terminal pickup',
      })],
    ])

    const carHireFuelTypeLabelMap = new Map<App.CarHireVehicleFuelType, string>([
      ['Diesel', intl.formatMessage({
        id: 'Nb4hCjrTeT',
        defaultMessage: 'Diesel',
        description: 'Car hire fuel type label for diesel vehicles',
      })],
      ['Electric', intl.formatMessage({
        id: 's7GfNxOEXn',
        defaultMessage: 'Electric',
        description: 'Car hire fuel type label for electric vehicles',
      })],
      ['Hybrid', intl.formatMessage({
        id: '6bLYjb2xF8',
        defaultMessage: 'Hybrid',
        description: 'Car hire fuel type label for hybrid vehicles',
      })],
      ['Petrol', intl.formatMessage({
        id: 'b1mLdNwMb3',
        defaultMessage: 'Petrol',
        description: 'Car hire fuel type label for petrol vehicles',
      })],
      ['MultiFuel', intl.formatMessage({
        id: '077eqgOb9I',
        defaultMessage: 'Multi-fuel',
        description: 'Car hire fuel type label for multi-fuel vehicles',
      })],
      ['Unspecified', intl.formatMessage({
        id: 'dX2sSXXBW7',
        defaultMessage: 'Unspecified',
        description: 'Car hire fuel type label for unspecified fuel type',
      })],
      ['PlugInHybrid', intl.formatMessage({
        id: '0VXJ/Mpbvn',
        defaultMessage: 'Plug-in hybrid',
        description: 'Car hire fuel type label for plug-in hybrid vehicles',
      })],
    ])

    return {
      pickupDropoffLocationSearchTitleLabel,
      dropoffLocationSearchTitleLabel,
      pickupLocationSearchTitleLabel,
      pickupLocationSearchPlaceholderLabel,
      carHireLuggageCategories,
      carHireSeatCategories,
      driverCategories,
      carHireFilterKeyToLabel,
      pickupTypeLabelMap,
      carHireFuelTypeLabelMap,
    }
  }, [intl])
}

export default useCarHireLabels
